import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { apiUrl } from '../apiUrl';

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
  mapUrl?: string;
}

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: '',
  });
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phone: '',
    address: '',
    mapUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const submissionPending = useRef(false);
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await fetch(apiUrl('/api/contact-info'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await res.json();
        if (res.ok) {
          setContactInfo({
            email: data.email,
            phone: data.phone,
            address: data.address,
            mapUrl: data.mapUrl,
          });
        } else {
          setError(data.message || 'Error fetching contact information.');
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Error fetching contact information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const closeModal = () => setShowModal(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionPending.current || uncertain) return;
    submissionPending.current = true;
    setValidationAttempted(false);
    setIsSubmitting(true);

    try {
      const res = await fetch(apiUrl('/api/contact-messages'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const confirmedFailure = res.status >= 500 && data?.message === 'Unable to save your enquiry and queue its notification. Your form details are still available; please try again later.';
        const message = (res.status >= 500 && !confirmedFailure)
          ? 'We could not confirm whether your enquiry was saved. Please contact the Foundation before retrying to avoid a duplicate.'
          : (data?.message || 'Unable to submit your enquiry. Please check the form and try again.');
        if (res.status >= 500 && !confirmedFailure) setUncertain(true);
        setResult({ kind: 'error', message });
        setShowModal(true);
        return;
      }
      if (!data || typeof data.emailQueued !== 'boolean') {
        setUncertain(true);
        setResult({ kind: 'error', message: 'We could not confirm whether your enquiry was saved. Please contact the Foundation before retrying to avoid a duplicate.' });
        setShowModal(true);
        return;
      }
      setResult({ kind: 'success', message: data.emailQueued
        ? 'Thank you for reaching out. Your enquiry has been saved and the notification queued; email delivery has not yet been confirmed.'
        : 'Your enquiry has been saved, but the email notification could not be queued. Please contact the Foundation directly if needed.' });
      setShowModal(true);
      setFormData({ name: '', email: '', mobile: '', subject: '', message: '' });
    } catch (err) {
      setUncertain(true);
      setResult({ kind: 'error', message: 'We could not confirm whether your enquiry was saved. Please contact the Foundation before retrying to avoid a duplicate.' });
      setShowModal(true);
    } finally {
      submissionPending.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sf-contact-page">
      <section className="sf-contact-intro tw-py-[clamp(3rem,6vw,5rem)]">
        <Container className="sf-contact-container">
          <p className="sf-contact-eyebrow">GET IN TOUCH</p>
          <h1>Contact Us</h1>
          <p className="sf-contact-lead">Have a question about the Foundation or our current education work? We would be glad to hear from you.</p>
        </Container>
      </section>
      <section className="tw-py-[clamp(2.8rem,5vw,4.4rem)]">
      <Container className="sf-contact-container">
        <Row className="gy-4">
          {/* Left Side Content */}
          <Col md={6} className="mb-4">
            <div className="sf-contact-card tw-h-full">
              <h2>Get in Touch</h2>
              <p className="contact-text">
                We’d love to hear from you! Whether you have questions, want to collaborate, or wish to support our mission, feel free to get in touch.
              </p>
              {loading ? (
                <p>Loading contact information...</p>
              ) : error ? (
                <p className="text-danger">{error}</p>
              ) : (
                <>
                  <p className="contact-detail">
                    <i className="fas fa-envelope me-2"></i> Email: {contactInfo.email}
                  </p>
                  <p className="contact-detail">
                    <i className="fas fa-phone me-2"></i> Phone: {contactInfo.phone}
                  </p>
                  <p className="contact-detail">
                    {/* <i className="fas fa-map-marker-alt me-2"></i> Address: {contactInfo.address} */}
                  </p>
                </>
              )}
            </div>
          </Col>

          {/* Right Side Form */}
          <Col md={6} className="mb-4">
            <div className="sf-contact-card sf-contact-form">
              <h2>Send an Enquiry</h2>
              <p>Fields marked * are required.</p>
              <Form onSubmit={handleSubmit} onInvalidCapture={() => setValidationAttempted(true)} className={validationAttempted ? 'sf-validation-attempted' : undefined}>
                {validationAttempted && <p className="sf-validation-summary" role="alert">Please correct the highlighted fields. Your browser will explain the first error.</p>}
                <Form.Group className="mb-3" controlId="formName">
                  <Form.Label>Name <span aria-hidden="true">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email <span aria-hidden="true">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formMobile">
                  <Form.Label>Mobile Number <span aria-hidden="true">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    pattern="[0-9]{10}"
                    aria-describedby="contact-mobile-help"
                    required
                  />
                  <Form.Text id="contact-mobile-help">Enter a 10-digit mobile number.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formSubject">
                  <Form.Label>Subject (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Enter subject"
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formMessage">
                  <Form.Label>Message <span aria-hidden="true">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    required
                  />
                </Form.Group>

                {uncertain && <p role="status">The submission result is uncertain. Please contact the Foundation before trying again.</p>}
                <Button variant="primary" type="submit" className="sf-contact-submit" disabled={isSubmitting || uncertain}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Form>
            </div>
          </Col>
        </Row>

        {/* Location Map Section */}
        {/* <Row className="mt-5">
          <Col>
            <h3 className="text-center mb-4 location-title">Our Location</h3>
            <div className="location-map">
              {loading ? (
                <p>Loading map...</p>
              ) : error || !contactInfo.mapUrl ? (
                <p className="text-danger">Map not available.</p>
              ) : (
                <iframe
                  src={contactInfo.mapUrl}
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="NGO Location Map"
                ></iframe>
              )}
            </div>
          </Col>
        </Row> */}

        {/* Success/Error Modal */}
        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Body className="text-center">
            {result?.kind === 'success' ? (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                <h4 className="text-success mb-2">Success!</h4>
                <p>{result.message}</p>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
                <h4 className="text-danger mb-2">Error!</h4>
                <p>{result?.message}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={closeModal}>Close</Button></Modal.Footer>
        </Modal>
      </Container>
      </section>
    </div>
  );
};

export default ContactSection;
